class ScfRoomTvCarousel extends HTMLElement {
  /** @type {Element[]} */
  slides = [];
  /** @type {Element[]} */
  dots = [];
  /** @type {Element | null} */
  counter = null;
  /** @type {number} */
  current = 0;
  /** @type {number} */
  intervalMs = 6000;
  /** @type {string} */
  transition = 'fade';
  /** @type {boolean} */
  pauseOnHover = true;
  /** @type {boolean} */
  reducedMotion = false;
  /** @type {ReturnType<typeof setInterval> | null} */
  timer = null;

  connectedCallback() {
    this.slides = [...this.querySelectorAll('.scf-room-tv__slide')];
    this.dots = [...this.querySelectorAll('.scf-room-tv__dot')];
    this.counter = this.querySelector('.scf-room-tv__counter');
    if (this.slides.length <= 1) return;

    this.current = 0;
    this.intervalMs = parseInt(this.dataset.interval || '6', 10) * 1000;
    this.transition = this.dataset.transition || 'fade';
    this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.classList.add(`scf-room-tv__carousel--${this.transition}`);

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
        this.restart();
      });
    });

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
    }

    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  /**
   * @param {number} index
   */
  goTo(index) {
    this.slides[this.current]?.classList.remove('is-active');
    this.dots[this.current]?.classList.remove('is-active');
    this.dots[this.current]?.setAttribute('aria-selected', 'false');

    this.current = index;

    this.slides[this.current]?.classList.add('is-active');
    this.dots[this.current]?.classList.add('is-active');
    this.dots[this.current]?.setAttribute('aria-selected', 'true');

    if (this.counter) {
      this.counter.textContent = `${this.current + 1} / ${this.slides.length}`;
    }
  }

  next() {
    if (!this.slides.length) return;
    this.goTo((this.current + 1) % this.slides.length);
  }

  start() {
    this.stop();
    if (this.reducedMotion || this.slides.length <= 1) return;
    this.timer = window.setInterval(() => this.next(), this.intervalMs);
  }

  stop() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    this.stop();
    this.start();
  }
}

if (!customElements.get('scf-room-tv-carousel')) {
  customElements.define('scf-room-tv-carousel', ScfRoomTvCarousel);
}

class ScfRoomTvVideos extends HTMLElement {
  /** @type {Element[]} */
  slides = [];
  /** @type {(HTMLVideoElement | null)[]} */
  videos = [];
  /** @type {number} */
  current = 0;
  /** @type {boolean} */
  reducedMotion = false;
  /** @type {(() => void) | null} */
  #onPrev = null;
  /** @type {(() => void) | null} */
  #onNext = null;

  connectedCallback() {
    this.slides = [...this.querySelectorAll('.scf-room-tv__video-slide')];
    if (!this.slides.length) return;

    this.videos = this.slides.map(
      (slide) => /** @type {HTMLVideoElement | null} */ (slide.querySelector('video'))
    );
    this.current = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.videos.forEach((video, index) => {
      if (!video) return;
      video.addEventListener('ended', () => this.next());
      if (index !== 0) {
        video.pause();
      }
    });

    const prevBtn = this.querySelector('[data-tv-video-prev]');
    const nextBtn = this.querySelector('[data-tv-video-next]');
    if (prevBtn) {
      this.#onPrev = () => this.prev();
      prevBtn.addEventListener('click', this.#onPrev);
    }
    if (nextBtn) {
      this.#onNext = () => this.next();
      nextBtn.addEventListener('click', this.#onNext);
    }

    if (!this.reducedMotion) {
      this.play(this.current);
    }
  }

  disconnectedCallback() {
    this.videos.forEach((video) => video?.pause());
    const prevBtn = this.querySelector('[data-tv-video-prev]');
    const nextBtn = this.querySelector('[data-tv-video-next]');
    if (prevBtn && this.#onPrev) prevBtn.removeEventListener('click', this.#onPrev);
    if (nextBtn && this.#onNext) nextBtn.removeEventListener('click', this.#onNext);
  }

  /**
   * @param {number} index
   */
  play(index) {
    const video = this.videos[index];
    if (!video) return;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  /**
   * @param {number} index
   */
  goTo(index) {
    if (!this.slides[index]) return;
    if (index === this.current) return;

    this.slides[this.current]?.classList.remove('is-active');
    this.videos[this.current]?.pause();

    this.current = index;
    this.slides[this.current]?.classList.add('is-active');

    const video = this.videos[this.current];
    if (video && !this.reducedMotion) {
      video.currentTime = 0;
      this.play(this.current);
    }
  }

  prev() {
    if (this.slides.length <= 1) return;
    this.goTo((this.current - 1 + this.slides.length) % this.slides.length);
  }

  next() {
    if (this.slides.length <= 1) return;
    this.goTo((this.current + 1) % this.slides.length);
  }
}

if (!customElements.get('scf-room-tv-videos')) {
  customElements.define('scf-room-tv-videos', ScfRoomTvVideos);
}
